import { useState, useEffect, useMemo } from 'react'
import { useAtom } from 'jotai'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material'
import {
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  HelpOutline,
} from '@mui/icons-material'
import { isAdminOrManager } from '../utils/roles'
import {
  companyContextApi,
  CompanyContextSection,
  CompanyContextSectionId,
} from '../services/api'
import { useNotification } from '../hooks/useNotification'
import { userAtom } from '../state/auth'
import HelpDrawer from '../components/HelpDrawer'

const SECTION_DESCRIPTIONS: Record<CompanyContextSectionId, string> = {
  'brand-identity': "Who MissFit is, the mission, and how we position ourselves.",
  'brand-voice': "Tone, voice, do's and don'ts — the way we talk to clients.",
  'service-offerings': 'Packages, deliverables, and what clients get from us.',
  process: 'The workflow stages clients move through and what to expect at each one.',
}

const SECTION_TOOLTIPS: Record<CompanyContextSectionId, string> = {
  'brand-identity':
    "Brand Identity — Who MissFit is, the mission, and how we position ourselves. Used by every AI agent (and the MissFit MCP server) as the 'who we are' anchor so outputs sound like MissFit, not a generic recruiter.",
  'brand-voice':
    "Brand Voice — Tone, do's and don'ts, and the way we talk to clients. Injected into every AI prompt so resumes, emails, LinkedIn copy, and chat replies all sound on-brand.",
  'service-offerings':
    'Service Offerings — Packages, deliverables, and what clients get. Used by sales and client-success agents so they describe pricing, scope, and outcomes accurately when talking to leads and clients.',
  process:
    'Process — The workflow stages clients move through and what to expect at each one. Used by agents to set client expectations and to know which stage-gate a client is at when answering questions.',
}

export default function CompanyContext() {
  const [user] = useAtom(userAtom)
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [sections, setSections] = useState<CompanyContextSection[]>([])
  const [activeTab, setActiveTab] = useState(0)
  const [draftContent, setDraftContent] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)

  const canEdit = isAdminOrManager(user)
  const currentSection = sections[activeTab]
  const currentDraft = currentSection ? draftContent[currentSection.sectionId] ?? '' : ''
  const isDirty = currentSection ? currentDraft !== currentSection.content : false

  useEffect(() => {
    loadSections()
  }, [])

  const loadSections = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await companyContextApi.list()
      setSections(data)
      const drafts: Record<string, string> = {}
      data.forEach((s) => {
        drafts[s.sectionId] = s.content || ''
      })
      setDraftContent(drafts)
    } catch (err) {
      console.error('Failed to load company context:', err)
      setError('Failed to load company context')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!currentSection) return
    try {
      setSaving(true)
      const updated = await companyContextApi.update({
        sectionId: currentSection.sectionId,
        title: currentSection.title,
        content: currentDraft,
      })
      setSections((prev) =>
        prev.map((s) => (s.sectionId === updated.sectionId ? updated : s))
      )
      setDraftContent((prev) => ({ ...prev, [updated.sectionId]: updated.content }))
      showSuccess(`Saved ${currentSection.title}`)
    } catch (err) {
      console.error('Failed to save section:', err)
      showError('Failed to save section')
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    if (!currentSection) return
    if (
      !confirm(
        `Clear all content for "${currentSection.title}"? This deletes the saved section and its history.`
      )
    ) {
      return
    }
    try {
      setSaving(true)
      await companyContextApi.clear(currentSection.sectionId)
      await loadSections()
      showSuccess(`Cleared ${currentSection.title}`)
    } catch (err) {
      console.error('Failed to clear section:', err)
      showError('Failed to clear section')
    } finally {
      setSaving(false)
    }
  }

  const handleRevert = () => {
    if (!currentSection) return
    setDraftContent((prev) => ({
      ...prev,
      [currentSection.sectionId]: currentSection.content || '',
    }))
  }

  const characterCount = useMemo(() => currentDraft.length, [currentDraft])

  if (!canEdit) {
    return (
      <Box p={3}>
        <Alert severity="error">Access denied. Admins and managers only.</Alert>
      </Box>
    )
  }

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4">Company Context</Typography>
            <Tooltip title="How does this page work?">
              <IconButton
                onClick={() => setHelpOpen(true)}
                size="small"
                aria-label="Open help drawer"
              >
                <HelpOutline />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            This content is automatically prepended to every AI agent's system prompt and is also
            served by the MissFit MCP server to off-platform Claude clients. Keep it focused —
            tone, positioning, what we sell, and how we work.
          </Typography>
        </Box>
        <IconButton onClick={loadSections} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sections.map((s) => (
              <Tab
                key={s.sectionId}
                label={
                  <Tooltip
                    title={SECTION_TOOLTIPS[s.sectionId] ?? SECTION_DESCRIPTIONS[s.sectionId] ?? ''}
                    placement="top"
                    arrow
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{s.title}</span>
                      {s.isEmpty ? (
                        <Chip label="empty" size="small" color="default" />
                      ) : (
                        <Chip label={`v${s.version}`} size="small" color="primary" />
                      )}
                    </Stack>
                  </Tooltip>
                }
              />
            ))}
          </Tabs>
          <CardContent>
            {currentSection && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {SECTION_DESCRIPTIONS[currentSection.sectionId]}
                </Typography>
                <TextField
                  multiline
                  minRows={18}
                  fullWidth
                  value={currentDraft}
                  onChange={(e) =>
                    setDraftContent((prev) => ({
                      ...prev,
                      [currentSection.sectionId]: e.target.value,
                    }))
                  }
                  placeholder={`Paste or write the ${currentSection.title.toLowerCase()} content here. Markdown is supported.`}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 14 } }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Typography variant="caption" color="text.secondary">
                    {characterCount.toLocaleString()} characters
                    {currentSection.updatedBy && (
                      <>
                        {' · '}last edited by {currentSection.updatedBy}
                        {currentSection.updatedAt &&
                          ` on ${new Date(currentSection.updatedAt).toLocaleString()}`}
                      </>
                    )}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {currentSection.history && currentSection.history.length > 0 && (
                      <Tooltip title="View history">
                        <Button
                          startIcon={<HistoryIcon />}
                          onClick={() => setHistoryOpen(true)}
                          size="small"
                        >
                          History ({currentSection.history.length})
                        </Button>
                      </Tooltip>
                    )}
                    {isDirty && (
                      <Button onClick={handleRevert} size="small" disabled={saving}>
                        Revert
                      </Button>
                    )}
                    {!currentSection.isEmpty && (
                      <Button
                        onClick={handleClear}
                        startIcon={<ResetIcon />}
                        size="small"
                        color="warning"
                        disabled={saving}
                      >
                        Clear
                      </Button>
                    )}
                    <Button
                      onClick={handleSave}
                      startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                      variant="contained"
                      disabled={saving || !isDirty}
                    >
                      Save
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{currentSection?.title} — version history</DialogTitle>
        <DialogContent dividers>
          {currentSection?.history?.map((v) => (
            <Box key={v.version} mb={3}>
              <Typography variant="subtitle2">
                v{v.version} · {v.updatedBy} ·{' '}
                {new Date(v.updatedAt).toLocaleString()}
              </Typography>
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontSize: 12,
                  mt: 1,
                  p: 1.5,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                {v.content}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <HelpDrawer
        articleId="company-context"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </Box>
  )
}
